import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Gift, Search, RefreshCw, ChevronDown, ChevronUp,
  Phone, Mail, Calendar, CheckCircle, Clock, Star, History,
  Edit2, Save, X, KeyRound, Copy, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [editingLoyalty, setEditingLoyalty] = useState(null);
  const [newLoyaltyCount, setNewLoyaltyCount] = useState(0);
  const [passwordResets, setPasswordResets] = useState([]);
  const [showResets, setShowResets] = useState(false);

  const [stats, setStats] = useState({
    totalCustomers: 0,
    freeEligible: 0,
    totalLoyaltyPoints: 0
  });

  const fetchPasswordResets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/password-resets`);
      if (response.data.success) {
        setPasswordResets(response.data.resets);
      }
    } catch (error) {
      console.error('Error fetching password resets:', error);
    }
  }, []);

  const fetchCustomers = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const response = await axios.get(`${API}/admin/customers`);
      
      if (response.data.success) {
        const customersData = response.data.customers;
        setCustomers(customersData);
        
        // Calculate stats
        setStats({
          totalCustomers: customersData.length,
          freeEligible: customersData.filter(c => c.free_service_eligible).length,
          totalLoyaltyPoints: customersData.reduce((sum, c) => sum + (c.loyalty_count || 0), 0)
        });
      }
      
      // Also fetch password resets
      fetchPasswordResets();
    } catch (error) {
      console.error('Error fetching customers:', error);
      if (!silent) {
        toast.error('Failed to load customers');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchCustomerHistory = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}/history`);
      if (response.data.success) {
        setCustomerHistory(response.data);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
      toast.error('Failed to load customer history');
    }
  };

  const toggleCustomerExpand = (customerId) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
      setCustomerHistory(null);
    } else {
      setExpandedCustomer(customerId);
      fetchCustomerHistory(customerId);
    }
  };

  const handleEditLoyalty = (customer) => {
    setEditingLoyalty(customer.id);
    setNewLoyaltyCount(customer.loyalty_count || 0);
  };

  const handleSaveLoyalty = async (customerId) => {
    try {
      const response = await axios.put(`${API}/admin/customers/${customerId}/loyalty`, {
        loyalty_count: parseInt(newLoyaltyCount)
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setEditingLoyalty(null);
        fetchCustomers(true);
        if (expandedCustomer === customerId) {
          fetchCustomerHistory(customerId);
        }
      }
    } catch (error) {
      console.error('Error updating loyalty:', error);
      toast.error(error.response?.data?.detail || 'Failed to update loyalty');
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const term = searchTerm.toLowerCase();
    return (
      customer.name.toLowerCase().includes(term) ||
      customer.email.toLowerCase().includes(term) ||
      customer.phone.includes(searchTerm)
    );
  });

  const getLoyaltyColor = (count) => {
    if (count >= 10) return 'bg-green-100 text-green-800 border-green-300';
    if (count >= 7) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (count >= 4) return 'bg-blue-100 text-blue-800 border-blue-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'picked-up': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
        <span className="ml-3 text-gray-600">Loading customers...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Users size={16} className="text-teal-600" />
              Total Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-teal-600">{stats.totalCustomers}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Gift size={16} className="text-green-600" />
              Free Service Eligible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.freeEligible}</div>
            <p className="text-sm text-gray-500">Ready for 11th FREE wash</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Star size={16} className="text-purple-600" />
              Total Loyalty Points
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{stats.totalLoyaltyPoints}</div>
            <p className="text-sm text-gray-500">Across all customers</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          onClick={() => fetchCustomers()}
          variant="outline"
          className="border-teal-600 text-teal-600 hover:bg-teal-50"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Password Reset Requests */}
      {passwordResets.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader 
            className="cursor-pointer"
            onClick={() => setShowResets(!showResets)}
          >
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-orange-700">
                <KeyRound size={20} />
                Password Reset Requests
                <Badge className="bg-orange-500 text-white ml-2">{passwordResets.length}</Badge>
              </div>
              {showResets ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </CardTitle>
            <CardDescription className="text-orange-600">
              Customers requesting password reset - share the link with them
            </CardDescription>
          </CardHeader>
          {showResets && (
            <CardContent>
              <div className="space-y-3">
                {passwordResets.map((reset) => {
                  const resetUrl = `${window.location.origin}/login?reset_token=${reset.reset_token}`;
                  return (
                    <div 
                      key={reset.id} 
                      className="p-4 bg-white rounded-lg border border-orange-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{reset.customer_name}</p>
                          <p className="text-sm text-gray-600">{reset.customer_email}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Requested: {formatDate(reset.created_at)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(resetUrl);
                              toast.success('Reset link copied to clipboard!');
                            }}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <Copy size={14} className="mr-1" />
                            Copy Link
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(resetUrl, '_blank')}
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          >
                            <ExternalLink size={14} className="mr-1" />
                            Open
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono break-all text-gray-600">
                        {resetUrl}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Customers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="text-teal-600" size={20} />
            Customer Loyalty Tracker
          </CardTitle>
          <CardDescription>
            {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p>No customers found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="border rounded-lg overflow-hidden hover:border-teal-300 transition-colors"
                >
                  {/* Customer Row */}
                  <div 
                    className="p-4 cursor-pointer bg-white hover:bg-gray-50"
                    onClick={() => toggleCustomerExpand(customer.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                          <span className="text-teal-600 font-bold text-lg">
                            {customer.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Mail size={14} />
                              {customer.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {customer.phone}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {/* Loyalty Progress */}
                        <div className="text-center">
                          <div className={`px-4 py-2 rounded-lg border ${getLoyaltyColor(customer.loyalty_count || 0)}`}>
                            <div className="text-2xl font-bold">{customer.loyalty_count || 0}/10</div>
                            <div className="text-xs">Loyalty</div>
                          </div>
                        </div>
                        
                        {/* Free Service Status */}
                        <div className="text-center min-w-[100px]">
                          {customer.free_service_eligible ? (
                            <Badge className="bg-green-500 text-white px-3 py-1">
                              🎁 FREE NEXT!
                            </Badge>
                          ) : (
                            <span className="text-sm text-gray-500">
                              {customer.washes_until_free} more to free
                            </span>
                          )}
                        </div>
                        
                        {/* Bookings Count */}
                        <div className="text-center">
                          <div className="text-lg font-semibold text-gray-700">{customer.total_bookings || 0}</div>
                          <div className="text-xs text-gray-500">Bookings</div>
                        </div>
                        
                        {/* Expand Icon */}
                        {expandedCustomer === customer.id ? (
                          <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={20} className="text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedCustomer === customer.id && (
                    <div className="border-t bg-gray-50 p-4">
                      {customerHistory ? (
                        <div className="space-y-4">
                          {/* Customer Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-sm text-gray-500">Total Bookings</div>
                              <div className="text-xl font-bold text-gray-800">
                                {customerHistory.summary.total_bookings}
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-sm text-gray-500">Completed</div>
                              <div className="text-xl font-bold text-green-600">
                                {customerHistory.summary.completed}
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-sm text-gray-500">Total Spent</div>
                              <div className="text-xl font-bold text-blue-600">
                                ${customerHistory.summary.total_spent.toFixed(2)}
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-sm text-gray-500">Free Services Used</div>
                              <div className="text-xl font-bold text-purple-600">
                                {customerHistory.summary.free_services_used}
                              </div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border">
                              <div className="text-sm text-gray-500">Member Since</div>
                              <div className="text-lg font-bold text-gray-700">
                                {formatDate(customerHistory.customer.member_since)}
                              </div>
                            </div>
                          </div>
                          
                          {/* Edit Loyalty */}
                          <div className="bg-white p-4 rounded-lg border">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Gift className="text-purple-600" size={20} />
                                <span className="font-semibold">Loyalty Progress</span>
                              </div>
                              
                              {editingLoyalty === customer.id ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    max="10"
                                    value={newLoyaltyCount}
                                    onChange={(e) => setNewLoyaltyCount(e.target.value)}
                                    className="w-20 text-center"
                                  />
                                  <span className="text-gray-500">/ 10</span>
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveLoyalty(customer.id)}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    <Save size={14} className="mr-1" />
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingLoyalty(null)}
                                  >
                                    <X size={14} />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-4">
                                  {/* Progress Bar */}
                                  <div className="w-48 bg-gray-200 rounded-full h-4 overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all"
                                      style={{ width: `${(customer.loyalty_count || 0) * 10}%` }}
                                    />
                                  </div>
                                  <span className="font-bold text-lg">{customer.loyalty_count || 0}/10</span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditLoyalty(customer)}
                                  >
                                    <Edit2 size={14} className="mr-1" />
                                    Edit
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Service History */}
                          <div className="bg-white rounded-lg border">
                            <div className="p-4 border-b">
                              <h4 className="font-semibold flex items-center gap-2">
                                <History className="text-teal-600" size={18} />
                                Service History
                              </h4>
                            </div>
                            <div className="max-h-64 overflow-y-auto">
                              {customerHistory.bookings.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                  No booking history
                                </div>
                              ) : (
                                <table className="w-full">
                                  <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                      <th className="text-left p-3 text-sm font-medium text-gray-600">Date</th>
                                      <th className="text-left p-3 text-sm font-medium text-gray-600">Pickup</th>
                                      <th className="text-left p-3 text-sm font-medium text-gray-600">City</th>
                                      <th className="text-left p-3 text-sm font-medium text-gray-600">Amount</th>
                                      <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                                      <th className="text-left p-3 text-sm font-medium text-gray-600">Free?</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {customerHistory.bookings.map((booking) => (
                                      <tr key={booking.id} className="border-t hover:bg-gray-50">
                                        <td className="p-3 text-sm">{formatDate(booking.createdAt)}</td>
                                        <td className="p-3 text-sm">{formatDate(booking.pickupDate)}</td>
                                        <td className="p-3 text-sm">{booking.city}</td>
                                        <td className="p-3 text-sm font-medium">
                                          ${(booking.amount || 0).toFixed(2)}
                                        </td>
                                        <td className="p-3">
                                          <Badge className={`${getStatusColor(booking.status)} text-xs`}>
                                            {booking.status}
                                          </Badge>
                                        </td>
                                        <td className="p-3">
                                          {booking.loyalty_applied && (
                                            <Badge className="bg-green-500 text-white text-xs">
                                              🎁 FREE
                                            </Badge>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 text-teal-600 animate-spin mr-2" />
                          Loading history...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerManagement;
